import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  Plus,
  Search,
  Loader2,
  Package,
  Play,
  Trash2,
  XCircle,
  Eye,
  MoreHorizontal,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { cn } from '@/lib/utils';
import { useLocations } from '@/hooks/useLocations';
import { useProducts } from '@/hooks/useProducts';
import { useLocationStore } from '@/store/location';
import {
  useInventoryCountsList,
  useInventoryCountsRealtime,
  useCreateCount,
  useStartCount,
  useCancelCount,
  useDeleteCount,
  usePurchaseOrdersForLinking,
  useGoodsTransfersForLinking,
} from './useInventoryCounts';
import { STATUS_CONFIG, formatRelativeTime, formatDateTime } from './utils';
import type { ListTab, CreateCountFormData, InventoryCount } from './types';

const INITIAL_FORM_DATA: CreateCountFormData = {
  location_id: '',
  name: '',
  count_type: 'full',
  include_inactive_products: false,
  scheduled_date: new Date().toISOString().split('T')[0],
  scheduled_time: '09:00',
  notes: '',
  product_ids: [],
  category_ids: [],
  linked_entity_type: null,
  purchase_order_id: '',
  goods_request_id: '',
  linked_entity_notes: '',
};

export default function InventoryCountsList() {
  const navigate = useNavigate();
  const { currentLocation } = useLocationStore();
  const { locations } = useLocations();
  const { categories } = useProducts();

  // Data
  useInventoryCountsRealtime();
  const { data: inventoryCounts = [], isLoading } = useInventoryCountsList();

  // Mutations
  const createCount = useCreateCount();
  const startCount = useStartCount();
  const cancelCount = useCancelCount();
  const deleteCount = useDeleteCount();

  // UI state
  const [activeTab, setActiveTab] = useState<ListTab>('due');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState<CreateCountFormData>({
    ...INITIAL_FORM_DATA,
    location_id: currentLocation?.id || '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  // Linkage queries
  const { data: purchaseOrders = [] } = usePurchaseOrdersForLinking(
    formData.location_id,
    formData.linked_entity_type === 'purchase_order'
  );
  const { data: goodsTransfers = [] } = useGoodsTransfersForLinking(
    formData.location_id,
    formData.linked_entity_type === 'goods_request'
  );

  // ── Tab filtering ──────────────────────────
  const now = new Date();

  const tabCounts = {
    due: inventoryCounts.filter((c) => {
      const scheduled = new Date(c.scheduled_at);
      return (c.status === 'scheduled' && scheduled <= now) || c.status === 'in_progress';
    }).length,
    upcoming: inventoryCounts.filter((c) => {
      const scheduled = new Date(c.scheduled_at);
      return c.status === 'scheduled' && scheduled > now;
    }).length,
    completed: inventoryCounts.filter((c) => c.status === 'completed').length,
    canceled: inventoryCounts.filter((c) => c.status === 'canceled').length,
  };

  const filteredCounts = inventoryCounts.filter((count) => {
    const scheduled = new Date(count.scheduled_at);
    switch (activeTab) {
      case 'due':
        return (count.status === 'scheduled' && scheduled <= now) || count.status === 'in_progress';
      case 'upcoming':
        return count.status === 'scheduled' && scheduled > now;
      case 'completed':
        return count.status === 'completed';
      case 'canceled':
        return count.status === 'canceled';
      default:
        return true;
    }
  });

  const tabs: { id: ListTab; label: string; count: number }[] = [
    { id: 'due', label: 'Due', count: tabCounts.due },
    { id: 'upcoming', label: 'Upcoming', count: tabCounts.upcoming },
    { id: 'completed', label: 'Completed', count: 0 },
    { id: 'canceled', label: 'Canceled', count: 0 },
  ];

  // ── Handlers ───────────────────────────────

  const resetForm = () => {
    setFormData({ ...INITIAL_FORM_DATA, location_id: currentLocation?.id || '' });
    setFormError(null);
  };

  const handleCreateCount = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.location_id) {
      setFormError('Please select a location');
      return;
    }

    if (
      formData.count_type === 'partial' &&
      formData.product_ids.length === 0 &&
      formData.category_ids.length === 0
    ) {
      setFormError('Please select products or categories for a partial count');
      return;
    }

    const result = await createCount.mutateAsync(formData);
    setCreateDialogOpen(false);
    resetForm();
    if (result?.id) {
      navigate(result.id);
    }
  };

  const handleStartAndNavigate = async (count: InventoryCount) => {
    if (count.status === 'scheduled') {
      await startCount.mutateAsync(count.id);
    }
    navigate(`${count.id}/count`);
  };

  // ── Render ─────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Inventory Counts</h2>
          <p className="text-sm text-muted-foreground">
            Create, schedule and complete counts to keep track of your inventory.
          </p>
        </div>
        <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Add inventory count
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b mb-6">
        <nav className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
              )}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Counts List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading counts...</span>
          </div>
        </div>
      ) : filteredCounts.length === 0 ? (
        <EmptyState tab={activeTab} onAdd={() => setCreateDialogOpen(true)} />
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Name
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Outlet
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Count
                </th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredCounts.map((count) => (
                <CountRow
                  key={count.id}
                  count={count}
                  activeTab={activeTab}
                  onView={() => navigate(count.id)}
                  onStart={() => handleStartAndNavigate(count)}
                  onContinue={() => navigate(`${count.id}/count`)}
                  onCancel={() => {
                    if (confirm('Cancel this inventory count?')) {
                      cancelCount.mutateAsync(count.id);
                    }
                  }}
                  onDelete={() => {
                    if (confirm('Delete this inventory count?')) {
                      deleteCount.mutateAsync(count.id);
                    }
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create Count Dialog ──────────────── */}
      <ResponsiveModal open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <ResponsiveModalContent maxWidth="lg">
          <form onSubmit={handleCreateCount}>
            <ResponsiveModalHeader>
              <ResponsiveModalTitle>Add inventory count</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Schedule a full or partial inventory count to maintain accurate inventory levels.
              </ResponsiveModalDescription>
            </ResponsiveModalHeader>

            <ResponsiveModalBody className="max-h-[60vh] overflow-y-auto">
              <div className="grid gap-6">
                {formError && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                    {formError}
                  </div>
                )}

                {/* General */}
                <div>
                  <h3 className="font-semibold mb-1">General</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Start an inventory count now or schedule one for the future.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start date</Label>
                      <Input
                        type="date"
                        value={formData.scheduled_date}
                        onChange={(e) =>
                          setFormData({ ...formData, scheduled_date: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Start time</Label>
                      <Input
                        type="time"
                        value={formData.scheduled_time}
                        onChange={(e) =>
                          setFormData({ ...formData, scheduled_time: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Outlet</Label>
                      <Select
                        value={formData.location_id}
                        onValueChange={(value) =>
                          setFormData({ ...formData, location_id: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select outlet..." />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.map((loc) => (
                            <SelectItem key={loc.id} value={loc.id}>
                              {loc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Count name</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                </div>

                {/* Count type */}
                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-1">Choose products to count</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    You can include inactive products, which are not available for sale but may still
                    be in stock.
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, count_type: 'partial' })}
                      className={cn(
                        'p-4 rounded-lg border-2 text-left transition-all',
                        formData.count_type === 'partial'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/50'
                      )}
                    >
                      <h4 className="font-semibold mb-1">Partial count</h4>
                      <p className="text-sm text-muted-foreground">
                        Specify the products to include in this inventory count.
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, count_type: 'full' })}
                      className={cn(
                        'p-4 rounded-lg border-2 text-left transition-all',
                        formData.count_type === 'full'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/50'
                      )}
                    >
                      <h4 className="font-semibold mb-1">Full count</h4>
                      <p className="text-sm text-muted-foreground">
                        Include all the products in this inventory count.
                      </p>
                    </button>
                  </div>

                  <div className="flex items-center gap-2 mb-4">
                    <p className="text-sm text-muted-foreground">
                      {formData.count_type === 'full'
                        ? 'All'
                        : formData.product_ids.length}{' '}
                      products will be counted, excluding inactive products
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id="include-inactive"
                      checked={formData.include_inactive_products}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, include_inactive_products: checked })
                      }
                    />
                    <Label htmlFor="include-inactive">Include inactive products.</Label>
                  </div>

                  {formData.count_type === 'partial' && (
                    <div className="mt-4 space-y-4">
                      <div className="space-y-2">
                        <Label>Filter by category</Label>
                        <Select
                          value={formData.category_ids[0] || ''}
                          onValueChange={(value) =>
                            setFormData({
                              ...formData,
                              category_ids: value ? [value] : [],
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a category..." />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((category) => (
                              <SelectItem key={category.id} value={category.id}>
                                {category.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Or search for specific products</Label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search for suppliers, brands, categories, tags, or SKUs"
                            className="pl-9"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Linkage */}
                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm font-medium">Link to Document (Optional)</Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Link this count to a purchase order or goods transfer for tracking
                  </p>

                  <div className="space-y-3">
                    <Select
                      value={formData.linked_entity_type || 'none'}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          linked_entity_type:
                            value === 'none'
                              ? null
                              : (value as 'purchase_order' | 'goods_request'),
                          purchase_order_id: '',
                          goods_request_id: '',
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No linkage</SelectItem>
                        <SelectItem value="purchase_order">Purchase Order</SelectItem>
                        <SelectItem value="goods_request">Goods Transfer</SelectItem>
                      </SelectContent>
                    </Select>

                    {formData.linked_entity_type === 'purchase_order' && (
                      <div className="space-y-2">
                        <Label className="text-sm">Purchase Order</Label>
                        <Select
                          value={formData.purchase_order_id}
                          onValueChange={(value) =>
                            setFormData({ ...formData, purchase_order_id: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a purchase order..." />
                          </SelectTrigger>
                          <SelectContent>
                            {purchaseOrders.length === 0 ? (
                              <div className="py-4 text-center text-sm text-muted-foreground">
                                No purchase orders found
                              </div>
                            ) : (
                              purchaseOrders.map((po) => (
                                <SelectItem key={po.id} value={po.id}>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{po.order_number}</span>
                                    <span className="text-muted-foreground">-</span>
                                    <span className="text-muted-foreground">
                                      {po.supplier?.name || 'Unknown'}
                                    </span>
                                    <Badge variant="outline" className="text-xs ml-1">
                                      {po.status}
                                    </Badge>
                                  </div>
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {formData.linked_entity_type === 'goods_request' && (
                      <div className="space-y-2">
                        <Label className="text-sm">Goods Transfer</Label>
                        <Select
                          value={formData.goods_request_id}
                          onValueChange={(value) =>
                            setFormData({ ...formData, goods_request_id: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a goods transfer..." />
                          </SelectTrigger>
                          <SelectContent>
                            {goodsTransfers.length === 0 ? (
                              <div className="py-4 text-center text-sm text-muted-foreground">
                                No goods transfers found
                              </div>
                            ) : (
                              goodsTransfers.map((transfer) => (
                                <SelectItem key={transfer.id} value={transfer.id}>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {transfer.transfer_number}
                                    </span>
                                    <span className="text-muted-foreground">-</span>
                                    <span className="text-muted-foreground text-xs">
                                      {transfer.from_location?.name} to{' '}
                                      {transfer.to_location?.name}
                                    </span>
                                    <Badge variant="outline" className="text-xs ml-1">
                                      {transfer.status?.replace('_', ' ')}
                                    </Badge>
                                  </div>
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {formData.linked_entity_type && (
                      <div className="space-y-2">
                        <Label className="text-sm">Linkage Notes</Label>
                        <Textarea
                          value={formData.linked_entity_notes}
                          onChange={(e) =>
                            setFormData({ ...formData, linked_entity_notes: e.target.value })
                          }
                          placeholder="Why is this count linked? (e.g., 'Verifying received items from PO-123')"
                          rows={2}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Add any notes about this count..."
                    rows={2}
                  />
                </div>
              </div>
            </ResponsiveModalBody>

            <ResponsiveModalFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="blaze" disabled={createCount.isPending}>
                {createCount.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Create count
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  );
}

// ──────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────

function CountRow({
  count,
  activeTab,
  onView,
  onStart,
  onContinue,
  onCancel,
  onDelete,
}: {
  count: InventoryCount;
  activeTab: ListTab;
  onView: () => void;
  onStart: () => void;
  onContinue: () => void;
  onCancel: () => void;
  onDelete: () => void;
}) {
  const statusConfig = STATUS_CONFIG[count.status];
  const StatusIcon = statusConfig.icon;

  return (
    <tr className="hover:bg-muted/30 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div>
            <button onClick={onView} className="font-medium text-sm text-primary hover:underline">
              {count.name || count.count_number}
            </button>
            {count.status === 'in_progress' && (
              <Badge className={cn('ml-2 gap-1 text-[10px] py-0', statusConfig.color)}>
                <StatusIcon className="h-2.5 w-2.5" />
                {statusConfig.label}
              </Badge>
            )}
            <p className="text-xs text-muted-foreground mt-0.5">
              {activeTab === 'completed' && count.completed_at
                ? formatDateTime(count.completed_at)
                : formatRelativeTime(count.scheduled_at)}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm">{count.location?.name}</td>
      <td className="px-4 py-3 text-sm">{count.count_type === 'full' ? 'Full' : 'Partial'}</td>
      <td className="px-2 py-3">
        <div className="flex items-center gap-2 justify-end">
          {count.status === 'scheduled' && (
            <Button variant="blaze" size="sm" className="gap-1" onClick={onStart}>
              <Play className="h-3.5 w-3.5" />
              Start
            </Button>
          )}
          {count.status === 'in_progress' && (
            <Button variant="blaze" size="sm" className="gap-1" onClick={onContinue}>
              <ClipboardList className="h-3.5 w-3.5" />
              Continue
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onView}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              {(count.status === 'scheduled' || count.status === 'in_progress') && (
                <DropdownMenuItem onClick={onCancel} className="text-amber-600">
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel
                </DropdownMenuItem>
              )}
              {count.status === 'canceled' && (
                <DropdownMenuItem onClick={onDelete} className="text-red-600">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
}

function EmptyState({ tab, onAdd }: { tab: ListTab; onAdd: () => void }) {
  const messages: Record<ListTab, { title: string; description: string }> = {
    due: {
      title: 'No inventory counts due',
      description: 'Schedule your first inventory count to maintain accurate stock levels.',
    },
    upcoming: {
      title: 'No upcoming inventory counts',
      description: 'You have no inventory counts scheduled for the future.',
    },
    completed: {
      title: 'No completed inventory counts',
      description: 'Completed counts will appear here after they are finished.',
    },
    canceled: {
      title: 'No canceled inventory counts',
      description: 'Canceled counts will appear here.',
    },
  };

  const msg = messages[tab];

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-center py-12">
          <ClipboardList className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="font-medium mb-2">{msg.title}</h3>
          <p className="text-sm text-muted-foreground mb-4">{msg.description}</p>
          {(tab === 'due' || tab === 'upcoming') && (
            <Button variant="blaze" className="gap-2" onClick={onAdd}>
              <Plus className="h-4 w-4" />
              Add inventory count
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
